import { useState } from 'react'
import { Alert, Box, Button, Dialog, Field, IconButton, Input, Text, VStack } from '@chakra-ui/react'
import { FiPlus, FiTrash2 } from 'react-icons/fi'
import { PageContainer } from '../components/shared/PageContainer'
import { Card } from '../components/shared/Card'
import { ResponsiveModal } from '../components/shared/ResponsiveModal'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared/ResponsiveTable'
import { StatusChip, type StatusVariant } from '../components/shared/StatusChip'
import { useAuth } from '../hooks/useAuth'
import { useAddSuppression, useRemoveSuppression, useSuppressions } from '../hooks/useCommunications'
import { extractApiMessage } from '../lib/apiError'
import { formatShortDate } from '../lib/formatDate'
import type { SuppressionReason, SuppressionRow } from '../types/communications'

// Reason chips: Unsubscribed/DoNotContact are opt-outs (warning), Bounced/Complained are
// delivery failures (danger), Manual is an organizer's own call (neutral) -- same idiom as
// EmailStatusChip's recipient-status mapping.
const REASON_VARIANT: Record<SuppressionReason, StatusVariant> = {
  Unsubscribed: 'warning',
  Bounced: 'danger',
  Complained: 'danger',
  Manual: 'neutral',
  DoNotContact: 'warning',
}

// Spec §7: removing an Unsubscribed/Complained row is Admin-only (and audited server-side);
// Manual/Bounced/DoNotContact rows any Organizer can remove. The backend enforces this
// regardless of what the client shows -- hiding the control for the disallowed case is
// UX-only, matching every other client-side role gate in this app (frontend/src/CLAUDE.md's
// "Security" note).
const ADMIN_ONLY_REMOVE_REASONS: SuppressionReason[] = ['Unsubscribed', 'Complained']

function canRemove(row: SuppressionRow, isAdmin: boolean): boolean {
  return isAdmin || !ADMIN_ONLY_REMOVE_REASONS.includes(row.reason)
}

/** Spec §8 `/settings/communications/suppressions` -- capped-take list (useSuppressions has no
 * server-side skip/offset, same "paginated" shape as EmailListPage/useEmailMessages), an
 * add-Manual form, and Admin-gated remove with a confirm dialog. */
export function EmailSuppressionsPage() {
  const { isAdmin } = useAuth()
  const { data, isLoading, error } = useSuppressions()
  const addSuppression = useAddSuppression()
  const removeSuppression = useRemoveSuppression()

  const [email, setEmail] = useState('')
  const [removeTarget, setRemoveTarget] = useState<SuppressionRow | null>(null)

  const handleAdd = () => {
    const trimmed = email.trim()
    if (!trimmed) return
    addSuppression.mutate({ email: trimmed }, { onSuccess: () => setEmail('') })
  }

  const closeRemove = () => setRemoveTarget(null)

  const confirmRemove = () => {
    if (!removeTarget) return
    removeSuppression.mutate(removeTarget.id, { onSuccess: closeRemove })
  }

  // Built inside the component (not a module-level const like EmailListPage's columns) because
  // the Actions column's visibility depends on `isAdmin` and closes over `setRemoveTarget`.
  const columns: ResponsiveColumn<SuppressionRow>[] = [
    { header: 'Email', mobileRole: 'title', cell: (r) => <Text fontWeight="medium">{r.email}</Text> },
    {
      header: 'Reason',
      mobileRole: 'badge',
      cell: (r) => <StatusChip variant={REASON_VARIANT[r.reason]}>{r.reason}</StatusChip>,
    },
    { header: 'Added', mobileRole: 'meta', cell: (r) => <Text fontSize="sm" color="fg.muted">{formatShortDate(r.createdAt)}</Text> },
    {
      header: 'Actions',
      mobileRole: 'badge',
      // Not ResponsiveTable's `rowActions` -- that renders the same action list on every row,
      // and this control must be entirely absent (not just disabled) on rows the signed-in
      // user isn't allowed to remove, per row's `reason`.
      cell: (r) =>
        canRemove(r, isAdmin) ? (
          <IconButton
            aria-label={`Remove ${r.email}`}
            size="sm"
            variant="ghost"
            onClick={() => setRemoveTarget(r)}
          >
            <FiTrash2 />
          </IconButton>
        ) : null,
    },
  ]

  return (
    <PageContainer title="Suppressions" subtitle="Addresses that will never receive email from this committee.">
      <Card mb={6}>
        <Text fontWeight="semibold" mb={3}>Add a manual suppression</Text>
        <Box display="flex" flexDirection={{ base: 'column', md: 'row' }} gap={3} alignItems={{ md: 'flex-end' }}>
          <Field.Root flex={1}>
            <Field.Label fontSize="sm" color="fg.muted">Email</Field.Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.org"
            />
          </Field.Root>
          <Button colorPalette="brand" onClick={handleAdd} disabled={!email.trim()} loading={addSuppression.isPending}>
            <FiPlus />Add
          </Button>
        </Box>
        {!!addSuppression.error && (
          <Alert.Root status="error" borderRadius="md" mt={3}>
            <Alert.Indicator />
            <Alert.Content>{extractApiMessage(addSuppression.error, 'Could not add this suppression.')}</Alert.Content>
          </Alert.Root>
        )}
      </Card>

      {!!error && <Text color="danger.fg" mb={4}>Could not load suppressions.</Text>}
      <ResponsiveTable
        columns={columns}
        rows={data ?? []}
        keyFn={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No suppressed addresses."
      />

      <ResponsiveModal isOpen={!!removeTarget} onClose={closeRemove}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content role="alertdialog">
            <Dialog.Header fontSize="lg" fontWeight="bold">Remove suppression</Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={3}>
                <Text>
                  Remove <strong>{removeTarget?.email}</strong> from suppressions? They may receive
                  email from this committee again.
                </Text>
                {!!removeSuppression.error && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    <Alert.Content>{extractApiMessage(removeSuppression.error, 'Could not remove this suppression.')}</Alert.Content>
                  </Alert.Root>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={closeRemove} disabled={removeSuppression.isPending}>Cancel</Button>
              <Button colorPalette="red" ml={3} onClick={confirmRemove} loading={removeSuppression.isPending}>
                Remove
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </PageContainer>
  )
}
